import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { TrendingUp } from "lucide-react"
import type { Lead } from "@crm-src/types/lead"
import { Money } from "@crm-src/components/money"

interface ConvertToCustomerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  lead: Lead | null
  onConfirm: () => void
}

export function ConvertToCustomerDialog({
  open,
  onOpenChange,
  lead,
  onConfirm,
}: ConvertToCustomerDialogProps) {
  return (
    <Dialog open={open} onClose={() => onOpenChange(false)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            height: 40,
            width: 40,
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            color: "success.main",
            bgcolor: (t) => alpha(t.palette.success.main, 0.12),
          }}
        >
          <TrendingUp size={20} />
        </Box>
        Convert to Customer
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ pt: 1 }}>
          {lead?.status !== 'qualified'
            ? `This prospect will first be qualified (current status: ${lead?.status}), then converted to a customer.`
            : "This will create a new customer and mark this prospect as converted."
          }
        </DialogContentText>
        {lead && (
          <Box sx={{ py: 2 }}>
            <Box sx={{ borderRadius: 1, border: 1, borderColor: "divider", bgcolor: "surfaceSoft", p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {lead.full_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {lead.email}
                  </Typography>
                  {lead.company_name && (
                    <Typography variant="body2" sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {lead.company_name}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ flexShrink: 0 }}>
                  <Box component="span" sx={{ fontFamily: "monospace", fontSize: "0.875rem", color: "text.secondary" }}>{lead.customer_id}</Box>
                </Box>
              </Box>
            </Box>

            {/* Financial wall: the prospected-value figure redacts to "—" for
                viewers without financial access. See components/money.tsx. */}
            <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
              Are you ready to convert <strong>{lead.full_name}</strong> to a customer?
              This will create a new customer with a prospected value of <strong><Money value={lead.prospected_value} /></strong>.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ gap: 1 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => onOpenChange(false)}
          sx={{ flex: 1 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={onConfirm}
          startIcon={<TrendingUp size={16} />}
          sx={{ flex: 1 }}
        >
          {lead?.status !== 'qualified'
            ? 'Qualify & Convert to Customer'
            : 'Convert to Customer'
          }
        </Button>
      </DialogActions>
    </Dialog>
  )
}
